import React, { useRef } from 'react';
import type { BaseProblem, GeneratedProblem, ProblemParts } from '../types';
import ProblemBodyRenderer from './ProblemBodyRenderer';
import LatexRenderer from './LatexRenderer';
import Spinner from './Spinner';

interface ProblemCardProps {
  problem: BaseProblem | GeneratedProblem;
  selectionMode?: boolean;
  isSelected?: boolean;
  onSelectToggle?: (id: string, shiftKey?: boolean) => void;
  onRegenerateExplanation?: (id: string) => void;
  onCancelExplanationGeneration?: (id: string) => void;
  onFixProblemData?: (id: string, isGenerated: boolean, target: 'problem' | 'explanation') => Promise<void>;
  onToggleLock?: (id: string, isGenerated: boolean, locked: boolean) => Promise<void>;
  shouldShake?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}

const ProblemCard: React.FC<ProblemCardProps> = ({
  problem,
  selectionMode,
  isSelected,
  onSelectToggle,
  onRegenerateExplanation,
  onCancelExplanationGeneration,
  onFixProblemData,
  onToggleLock,
  shouldShake,
  onClick
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const isGenerated = 'generatedParts' in problem;
  const parts = isGenerated ? (problem as GeneratedProblem).generatedParts : (problem as BaseProblem).parts;
  const croppedImage = !isGenerated ? (problem as BaseProblem).croppedImage : (problem as GeneratedProblem).baseProblemCroppedImage;
  const isLocked = problem.isLocked !== false; // Default to true (locked) if undefined

  // Determine visual feedback for explanation generation
  const isExplanationGenerating = problem.explanation === '__processing__';

  const handleCardClick = (e: React.MouseEvent) => {
    if (onClick) {
        onClick(e);
    } else if (selectionMode && onSelectToggle) {
        onSelectToggle(problem.id, e.shiftKey);
    }
  };

  const handleLockToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onToggleLock) {
          onToggleLock(problem.id, isGenerated, !isLocked);
      }
  };

  // Resolve Image source safely
  const figureImage = parts.figureImage || croppedImage || undefined;

  return (
    <div 
      ref={cardRef}
      className={`bg-[var(--color-bg-glass)] border border-[var(--color-border-glass)] rounded-lg flex flex-col relative transition-all duration-200 backdrop-blur-md group ${selectionMode ? 'cursor-pointer' : ''} ${isSelected ? 'ring-2 ring-offset-2 ring-offset-[var(--color-bg-primary)] ring-[var(--color-accent)]' : 'hover:border-[var(--color-text-secondary)]/50'} ${shouldShake ? 'animate-shake' : ''}`}
      onClick={handleCardClick}
      data-selectable-item={selectionMode ? "true" : undefined}
    >
      {/* Header / Metadata */}
      <div className="p-3 border-b border-[var(--color-border-glass)] flex justify-between items-center bg-[var(--color-bg-secondary)]/30 rounded-t-lg">
        <div className="flex items-center gap-2 min-w-0">
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${isGenerated ? 'bg-purple-900/30 text-purple-300 border-purple-700/50' : 'bg-indigo-900/30 text-indigo-300 border-indigo-700/50'}`}>
                {isGenerated ? 'AI 변형' : '기반 문제'}
            </span>
            <span className="text-xs text-[var(--color-text-tertiary)] truncate max-w-[150px]">
                {new Date(problem.createdAt).toLocaleDateString()}
            </span>
            {isLocked && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-[var(--color-text-tertiary)]" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
            )}
        </div>
        
        {/* Lock Button (Visible on hover or if unlocked?) - Actually user wants explicit toggle usually */}
        <button 
            onClick={handleLockToggle}
            className={`p-1 rounded hover:bg-white/10 transition-opacity ${isLocked ? 'text-[var(--color-text-tertiary)]' : 'text-[var(--color-text-secondary)] opacity-0 group-hover:opacity-100'}`}
            title={isLocked ? "잠금 해제" : "잠금 (편집/삭제 방지)"}
        >
            {isLocked ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                </svg>
            )}
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
          {/* Problem Body */}
          <div className="bg-[var(--color-sketchbook-bg)] text-[var(--color-sketchbook-text)] border border-[var(--color-sketchbook-border)] rounded-md p-4 sketchbook relative overflow-hidden">
              <ProblemBodyRenderer lines={parts.lines} figureImage={figureImage} />
              {parts.type === '객관식' && parts.choices && (
                  <div className="mt-4 pt-4 border-t border-gray-200/50">
                      <LatexRenderer latex={parts.choices} />
                  </div>
              )}
          </div>

          {/* Explanation */}
          {problem.explanation && (
              <div className={`bg-[var(--color-sketchbook-bg)] text-[var(--color-sketchbook-text)] border border-[var(--color-sketchbook-border)] rounded-md p-4 sketchbook text-sm relative ${isExplanationGenerating ? 'opacity-70' : ''}`}>
                  <div className="absolute top-2 right-2 bg-gray-200 text-gray-600 text-[10px] font-bold px-1.5 py-0.5 rounded">해설</div>
                  {isExplanationGenerating ? (
                      <div className="flex flex-col items-center justify-center py-4">
                          <Spinner small />
                          <span className="text-xs mt-2 text-gray-500">해설 작성 중...</span>
                          {onCancelExplanationGeneration && (
                              <button 
                                onClick={(e) => { e.stopPropagation(); onCancelExplanationGeneration(problem.id); }}
                                className="mt-2 text-xs text-red-500 hover:underline"
                              >
                                  취소
                              </button>
                          )}
                      </div>
                  ) : (
                      <LatexRenderer latex={problem.explanation} preserveLinebreaks />
                  )}
              </div>
          )}
          
          {/* Empty Explanation Placeholder / Generate Button */}
          {!problem.explanation && !isExplanationGenerating && onRegenerateExplanation && (
              <button 
                onClick={(e) => { e.stopPropagation(); onRegenerateExplanation(problem.id); }}
                className="w-full py-2 border-2 border-dashed border-[var(--color-border)] rounded-md text-xs text-[var(--color-text-tertiary)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-colors flex items-center justify-center gap-1"
              >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                  </svg>
                  해설 생성하기
              </button>
          )}
      </div>
    </div>
  );
};

export default React.memo(ProblemCard);